import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { CircleCheck } from "lucide-react";
import { useTheme } from "../../utils/theme";
import Layout from "../layout/Layout";
import { useDocumentTitle } from "../../utils/Common";
import { sendVerificationOtpService } from "../../api/auth";

export default function Profile() {
  const userDetail = useSelector((state) => state.auth.userDetail);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [sendingType, setSendingType] = useState(null);
  const [verificationError, setVerificationError] = useState("");
  const [isEditingEmail, setIsEditingEmail] = useState(false);
  const [email, setEmail] = useState(userDetail.email || "");

  useDocumentTitle("My Profile");
  const { isDark } = useTheme();

  useEffect(() => {
    if (!verificationError) return undefined;

    const timer = window.setTimeout(() => {
      setVerificationError("");
    }, 5000);

    return () => window.clearTimeout(timer);
  }, [verificationError]);

  useEffect(() => {
    if (!isEditingEmail) setEmail(userDetail.email || "");
  }, [isEditingEmail, userDetail.email]);

  const handleVerify = async (type) => {
    const payload =
      type === "email"
        ? {
            type: "email",
            email: userDetail.email,
          }
        : {
            type: "mobile",
            countryCode: userDetail.countryCode || "+91",
            mobile: userDetail.mobile,
            otpVia: "text",
          };

    try {
      setVerificationError("");
      setSendingType(type);
      if (type === "email") {
        sessionStorage.removeItem("pending-verification-email");
      }
      await sendVerificationOtpService(payload);
      sessionStorage.setItem(
        `verification-otp-resend-until:${type}`,
        String(Date.now() + 120000),
      );
      navigate(`/verify/${type}`);
    } catch (error) {
      setVerificationError(
        error.response?.data?.message ||
          error.message ||
          "Unable to send verification OTP. Please try again.",
      );
    } finally {
      setSendingType(null);
    }
  };

  const handleEmailChange = async () => {
    const nextEmail = email.trim();
    const isValidEmail = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(nextEmail);

    if (!isValidEmail) {
      setVerificationError("Please enter a valid email address.");
      return;
    }

    if (nextEmail.toLowerCase() === String(userDetail.email || "").toLowerCase()) {
      setIsEditingEmail(false);
      return;
    }

    try {
      setVerificationError("");
      setSendingType("email");
      await sendVerificationOtpService({ type: "email", email: nextEmail });
      sessionStorage.setItem("pending-verification-email", nextEmail);
      sessionStorage.setItem(
        "verification-otp-resend-until:email",
        String(Date.now() + 120000),
      );
      navigate("/verify/email");
    } catch (error) {
      setVerificationError(
        error.response?.data?.message ||
          error.message ||
          "Unable to update the email address. Please try again.",
      );
    } finally {
      setSendingType(null);
    }
  };

  return (
    <Layout>
      <section className={isDark ? "welcome-hero-dark-section mt-md-5":"welcome-hero-light mt-5"}>
        <div className="container py-5">
          <div className="row">
            <div className="col-md-12">
              <div className={isDark ? "welcome-hero-dark rounded-3 p-3":"welcome-hero-light-section rounded-3 p-3"}>
                <h3 className="fw-bold mb-3 text-body-emphasis">My Profile</h3>
                <p className="fw-bold">Personal Information</p>
                <hr className={isDark ? "welcome-hero-dark-border":"welcome-hero-light-border"}/>
                <div className="row">
                  <div className="col-md-3">
                    <div className="p-2">
                      <small className="text-secondary">Name</small>
                      <div>{userDetail.firstName}</div>
                    </div>
                  </div>
                  <div className="col-md-2">
                    <div className="p-2">
                      <small className="text-secondary">Mobile</small>
                      <div className="d-flex align-items-center gap-1">
                        <span>{userDetail.mobile}</span>
                        {userDetail.mobileVerifiedAt != null && (
                          <CircleCheck
                            size={17}
                            className="text-success"
                            aria-label="Mobile verified"
                          />
                        )}
                      </div>
                      {userDetail.mobileVerifiedAt === null && (
                        <button
                          type="button"
                          onClick={() => handleVerify("mobile")}
                          disabled={sendingType !== null}
                          className="btn btn-sm btn-outline-success mt-2"
                        >
                          {sendingType === "mobile" ? "Sending..." : "Verify"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-4">
                    <div className="p-2">
                      <small className="text-secondary">Email</small>
                      {isEditingEmail ? (
                        <div className="mt-1">
                          <input
                            type="email"
                            className="form-control form-control-sm"
                            value={email}
                            onChange={(event) => setEmail(event.target.value)}
                            disabled={sendingType !== null}
                            aria-label="Email address"
                          />
                          <div className="d-flex gap-2 mt-2">
                            <button
                              type="button"
                              className="btn btn-sm btn-success"
                              onClick={handleEmailChange}
                              disabled={sendingType !== null}
                            >
                              {sendingType === "email" ? "Saving..." : "Save & verify"}
                            </button>
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-secondary"
                              onClick={() => setIsEditingEmail(false)}
                              disabled={sendingType !== null}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="d-flex align-items-center gap-1">
                            <span>{userDetail.email}</span>
                            {userDetail.emailVerifiedAt != null && (
                              <CircleCheck
                                size={17}
                                className="text-success"
                                aria-label="Email verified"
                              />
                            )}
                          </div>
                          <button
                            type="button"
                            onClick={() => setIsEditingEmail(true)}
                            disabled={sendingType !== null}
                            className="btn btn-sm btn-outline-secondary mt-2 me-2"
                          >
                            Change email
                          </button>
                        </>
                      )}
                      {!isEditingEmail && userDetail.emailVerifiedAt == null && (
                        <button
                          type="button"
                          onClick={() => handleVerify("email")}
                          disabled={sendingType !== null}
                          className="btn btn-sm btn-outline-success mt-2"
                        >
                          {sendingType === "email" ? "Sending..." : "Verify"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="col-md-3">
                    <div className="p-2">
                      <small className="text-secondary">Date of birth</small>
                      <div>{userDetail.dob}</div>
                    </div>
                  </div>
                </div>
                {verificationError && (
                  <div className="alert alert-danger mt-3 mb-0" role="alert">
                    {verificationError}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </section>
    </Layout>
  );
}
